/**
 * `toTicks(calendar, input)` (1.K-1 deliverable 5). Never throws. Each side resolves to its unit
 * range by its precision, then, in order: the era step, the part, the qualifier; then the range
 * and the open sides. The result maps one to one onto a `time_point`'s derived columns and always
 * satisfies the four `tick_status` CHECK constraints of Base §3 with TICK_MIN ≤ lo < hi ≤ TICK_MAX.
 */
import { isTick, PARTS_OF_UNIT, PRECISIONS, QUALIFIERS, TICK_MAX, TICK_MIN, } from '@codex/contracts';
import { absoluteYearOf, eraByKey } from './eras.js';
import { add, floorDiv, floorMod, mul, sub } from './math.js';
import { isCompiled, } from './types.js';
import { monthLength, monthOffset, yearIsLeap, yearLength, yearStartTick } from './year.js';
const fail = (reason) => ({ ok: false, reason });
const invalid = (reason) => ({ status: 'invalid', reason });
function own(raw, key) {
    return Object.hasOwn(raw, key) ? raw[key] : undefined;
}
export function isPartsObject(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
/** `{}` on a side: no part present (a key whose value is undefined is no part). */
export function isEmptyParts(raw) {
    return Object.values(raw).every((v) => v === undefined);
}
/** A present string part, `undefined` when absent, or `null` when present with the wrong type. */
function str(raw, key) {
    const v = own(raw, key);
    return v === undefined || typeof v === 'string' ? v : null;
}
/** A present safe-integer part, `undefined` when absent, or `null` when not a safe integer. */
function int(raw, key) {
    const v = own(raw, key);
    return v === undefined || (typeof v === 'number' && Number.isSafeInteger(v)) ? v : null;
}
function monthByKey(calendar, key) {
    return calendar.months.find((m) => m.key === key);
}
/** The unit's range in ticks, before the era step; NaN where a value is not a safe integer. */
export function unitRange(calendar, unit) {
    const spd = calendar.secondsPerDay;
    switch (unit.kind) {
        case 'years': {
            const a = absoluteYearOf(unit.era, unit.first);
            const b = absoluteYearOf(unit.era, add(unit.first, unit.size - 1));
            return {
                lo: yearStartTick(calendar, Math.min(a, b)),
                hi: yearStartTick(calendar, add(Math.max(a, b), 1)),
            };
        }
        case 'season': {
            const season = calendar.seasons[unit.index];
            const last = unit.index === calendar.seasons.length - 1;
            const next = calendar.seasons[last ? 0 : unit.index + 1];
            return {
                lo: add(yearStartTick(calendar, unit.year), mul(season.startDay, spd)),
                hi: add(yearStartTick(calendar, add(unit.year, last ? 1 : 0)), mul(next.startDay, spd)),
            };
        }
        case 'month': {
            const leap = yearIsLeap(calendar, unit.year);
            const lo = add(yearStartTick(calendar, unit.year), mul(monthOffset(unit.month, leap), spd));
            return { lo, hi: add(lo, mul(monthLength(unit.month, leap), spd)) };
        }
        default:
            return { lo: unit.lo, hi: unit.hi };
    }
}
/**
 * The unit `k` units later in time (earlier for negative `k`); exact spans do not shift. A shift
 * whose position is not a safe integer gives a unit whose range is NaN, which `widen` clamps to the
 * tick bound, as it does for any range that leaves the safe integers.
 */
export function shiftUnit(calendar, unit, k) {
    switch (unit.kind) {
        case 'years': {
            // Era years run backwards in an era that counts down.
            const step = mul(unit.era.direction === 'up' ? k : -k, unit.size);
            return { ...unit, first: add(unit.first, step) };
        }
        case 'season': {
            // "c." shifts a season by one occurrence, so `at` stays a small integer.
            const at = add(unit.index, k);
            const n = calendar.seasons.length;
            return { kind: 'season', year: add(unit.year, floorDiv(at, n)), index: floorMod(at, n) };
        }
        case 'month': {
            // Regular months only are counted; an intercalary month sits between the regular month
            // before it (its first step back) and the one after it (its first step on).
            const r = calendar.regularMonths.length;
            const before = calendar.regularMonths.filter((m) => m.index < unit.month.index).length;
            const at = unit.month.intercalary ? add(before, k > 0 ? k - 1 : k) : add(before, k);
            if (!Number.isSafeInteger(at))
                return { ...unit, year: Number.NaN };
            const month = calendar.regularMonths[floorMod(at, r)];
            return { kind: 'month', year: add(unit.year, floorDiv(at, r)), month };
        }
        default:
            return unit;
    }
}
/**
 * The first, middle or last third of a range: a unit of n ≥ 3 whole days splits at day ⌊n/3⌋ and
 * day ⌊2n/3⌋; a shorter unit splits in whole seconds the same way; a unit of fewer than three
 * seconds keeps its whole range.
 */
export function applyPart(calendar, range, part) {
    if (part === null)
        return range;
    const spd = calendar.secondsPerDay;
    const length = sub(range.hi, range.lo);
    const days = length / spd;
    let a;
    let b;
    if (floorMod(range.lo, spd) === 0 && Number.isInteger(days) && days >= 3) {
        a = mul(floorDiv(days, 3), spd);
        b = mul(floorDiv(mul(days, 2), 3), spd);
    }
    else if (length >= 3) {
        a = floorDiv(length, 3);
        b = floorDiv(mul(length, 2), 3);
    }
    else {
        return range;
    }
    if (part === 'early')
        return { lo: range.lo, hi: add(range.lo, a) };
    if (part === 'mid')
        return { lo: add(range.lo, a), hi: add(range.lo, b) };
    return { lo: add(range.lo, b), hi: range.hi };
}
/** How many units "c." widens by, at each precision that shifts whole units. */
function approxUnits(calendar, unit) {
    if (unit.kind === 'years') {
        if (unit.size === 1)
            return calendar.approx.year;
        return unit.size === 10 ? calendar.approx.decade : 1;
    }
    return unit.kind === 'month' ? calendar.approx.month : 1;
}
/**
 * `approximate`: the side resolved again with its unit shifted n units earlier and n units later,
 * keeping its part and skipping the era step, from the start of the earlier to the end of the
 * later; an exact span widens both ends by its seconds. A widened end beyond the tick bounds is
 * clamped to them.
 */
function widen(calendar, unit, part, base) {
    let lo;
    let hi;
    if (unit.kind === 'span') {
        lo = sub(base.lo, unit.widen);
        hi = add(base.hi, unit.widen);
    }
    else {
        const n = approxUnits(calendar, unit);
        lo = applyPart(calendar, unitRange(calendar, shiftUnit(calendar, unit, -n)), part).lo;
        hi = applyPart(calendar, unitRange(calendar, shiftUnit(calendar, unit, n)), part).hi;
    }
    return { lo: isTick(lo) ? lo : TICK_MIN, hi: isTick(hi) ? hi : TICK_MAX };
}
/** Whether `raw` names a month (regular) or an intercalary month, checked for type and key. */
function readMonth(calendar, raw) {
    const month = str(raw, 'month');
    const intercalary = str(raw, 'intercalary');
    if (month === null || intercalary === null)
        return fail('incomplete_parts');
    if (month !== undefined && intercalary !== undefined)
        return fail('incomplete_parts');
    const key = month ?? intercalary;
    if (key === undefined)
        return { ok: true, month: null };
    const found = monthByKey(calendar, key);
    if (found === undefined || found.intercalary !== (intercalary !== undefined)) {
        return fail('unknown_key');
    }
    return { ok: true, month: found };
}
/** The unit of a side at every precision but `era`, before the era step. */
export function locateUnit(calendar, era, raw, precision) {
    const eraYear = int(raw, 'year');
    if (eraYear === undefined || eraYear === null)
        return fail('incomplete_parts');
    if (precision === 'year' || precision === 'decade' || precision === 'century') {
        const size = precision === 'year' ? 1 : precision === 'decade' ? 10 : 100;
        return {
            ok: true,
            unit: { kind: 'years', era, first: mul(floorDiv(eraYear, size), size), size },
        };
    }
    const year = absoluteYearOf(era, eraYear);
    if (precision === 'season') {
        const key = str(raw, 'season');
        if (key === undefined || key === null)
            return fail('incomplete_parts');
        const index = calendar.seasons.findIndex((s) => s.key === key);
        if (index < 0)
            return fail('unknown_key');
        return { ok: true, unit: { kind: 'season', year, index } };
    }
    if (precision === 'week')
        return weekUnit(calendar, raw, year);
    const read = readMonth(calendar, raw);
    if (!read.ok)
        return read;
    if (read.month === null)
        return fail('incomplete_parts');
    const leap = yearIsLeap(calendar, year);
    if (precision === 'month') {
        if (monthLength(read.month, leap) === 0)
            return fail('not_in_year');
        return { ok: true, unit: { kind: 'month', year, month: read.month } };
    }
    return dayUnit(calendar, raw, year, read.month, precision);
}
function weekUnit(calendar, raw, year) {
    const week = int(raw, 'week');
    const monthKey = str(raw, 'month');
    if (week === undefined || week === null || monthKey === null)
        return fail('incomplete_parts');
    const month = monthKey === undefined ? null : monthByKey(calendar, monthKey);
    if (month === undefined || month?.intercalary)
        return fail('unknown_key');
    const leap = yearIsLeap(calendar, year);
    const offset = month === null ? 0 : monthOffset(month, leap);
    const length = month === null ? yearLength(calendar, year) : monthLength(month, leap);
    const w = calendar.weekdays.length;
    const first = mul(w, sub(week, 1));
    if (!(week >= 1 && first < length))
        return fail('week_out_of_range');
    const last = Math.min(first + w, length);
    const start = yearStartTick(calendar, year);
    const spd = calendar.secondsPerDay;
    return {
        ok: true,
        unit: {
            kind: 'span',
            lo: add(start, mul(offset + first, spd)),
            hi: add(start, mul(offset + last, spd)),
            widen: mul(w, spd),
        },
    };
}
function dayUnit(calendar, raw, year, month, precision) {
    const day = int(raw, 'day');
    const hour = precision === 'day' ? 0 : int(raw, 'hour');
    const minute = precision === 'minute' ? int(raw, 'minute') : 0;
    if (day === null ||
        hour === undefined ||
        hour === null ||
        minute === undefined ||
        minute === null) {
        return fail('incomplete_parts');
    }
    if (day === undefined && !month.intercalary)
        return fail('incomplete_parts');
    const leap = yearIsLeap(calendar, year);
    const length = monthLength(month, leap);
    if (length === 0)
        return fail('not_in_year');
    const d = day ?? 1;
    if (d < 1 || d > length)
        return fail('day_out_of_range');
    if (hour < 0 || hour >= calendar.hoursPerDay || minute < 0 || minute >= calendar.minutesPerHour) {
        return fail('time_out_of_range');
    }
    const spd = calendar.secondsPerDay;
    const dayStart = add(yearStartTick(calendar, year), mul(monthOffset(month, leap) + d - 1, spd));
    const lo = add(dayStart, add(mul(hour, calendar.secondsPerHour), mul(minute, calendar.secondsPerMinute)));
    const size = precision === 'day'
        ? spd
        : precision === 'hour'
            ? calendar.secondsPerHour
            : calendar.secondsPerMinute;
    const widen = precision === 'day'
        ? mul(calendar.approx.day, spd)
        : precision === 'hour'
            ? calendar.secondsPerHour
            : calendar.secondsPerMinute;
    return { ok: true, unit: { kind: 'span', lo, hi: add(lo, size), widen } };
}
/** One side of a time point resolved at its precision and qualifier. */
function resolveSide(calendar, raw, precision, qualifier) {
    const part = own(raw, 'part');
    const eraKey = str(raw, 'era');
    if (eraKey === null || (part !== undefined && !PARTS_OF_UNIT.includes(part))) {
        return fail('incomplete_parts');
    }
    if (precision === 'era') {
        // An unbounded era is a property of the calendar, not a date authored as open.
        if (eraKey === undefined || part !== undefined)
            return fail('incomplete_parts');
        const era = eraByKey(calendar, eraKey);
        if (era === undefined)
            return fail('unknown_key');
        return { ok: true, lo: era.start ?? TICK_MIN, hi: era.end ?? TICK_MAX };
    }
    const era = eraKey === undefined ? calendar.defaultEra : eraByKey(calendar, eraKey);
    if (era === undefined)
        return fail('unknown_key');
    const located = locateUnit(calendar, era, raw, precision);
    if (!located.ok)
        return located;
    const base = unitRange(calendar, located.unit);
    if (!isTick(base.lo) || !isTick(base.hi))
        return fail('out_of_tick_range');
    const clipped = {
        lo: era.start === null ? base.lo : Math.max(base.lo, era.start),
        hi: era.end === null ? base.hi : Math.min(base.hi, era.end),
    };
    if (clipped.lo >= clipped.hi)
        return fail('outside_era');
    const parted = applyPart(calendar, clipped, part ?? null);
    const range = qualifier === 'approximate'
        ? widen(calendar, located.unit, part ?? null, parted)
        : parted;
    return { ok: true, ...range };
}
/**
 * The derived ticks of a time point's authored columns: `{status: 'ok', lo, hi}`, `open_lo`,
 * `open_hi`, or `invalid` with one of 1.0b's `TICK_REVIEW_REASONS`.
 */
export function toTicks(calendar, input) {
    if (!isCompiled(calendar))
        return invalid('calendar_invalid');
    if (!isPartsObject(input))
        return invalid('incomplete_parts');
    const precision = own(input, 'precision');
    const qualifier = own(input, 'qualifier');
    const loParts = own(input, 'loParts');
    const hiParts = own(input, 'hiParts') ?? null;
    if (!PRECISIONS.includes(precision) ||
        !QUALIFIERS.includes(qualifier) ||
        !isPartsObject(loParts) ||
        !(hiParts === null || isPartsObject(hiParts))) {
        return invalid('incomplete_parts');
    }
    const loOpen = isEmptyParts(loParts);
    const hiOpen = hiParts !== null && isEmptyParts(hiParts);
    if (loOpen && (hiParts === null || hiOpen))
        return invalid('incomplete_parts');
    const lo = loOpen ? null : resolveSide(calendar, loParts, precision, qualifier);
    if (lo !== null && !lo.ok)
        return invalid(lo.reason);
    const hi = hiParts === null || hiOpen ? null : resolveSide(calendar, hiParts, precision, qualifier);
    if (hi !== null && !hi.ok)
        return invalid(hi.reason);
    if (lo === null)
        return { status: 'open_lo', hi: hi.hi };
    if (hiOpen)
        return { status: 'open_hi', lo: lo.lo };
    const end = hi === null ? lo.hi : hi.hi;
    if (lo.lo >= end)
        return invalid('inverted_range');
    return { status: 'ok', lo: lo.lo, hi: end };
}
//# sourceMappingURL=ticks.js.map